import { createReducer } from '@reduxjs/toolkit'

import { CampaignActionTypes } from '@store/campaigns'
import { startDiscoveryRunSuccess } from '@store/discoveryRuns/actions'
import {
  agentSourcedCreatorActionFailure,
  agentSourcedCreatorActionSuccess,
  approveAgentSourcedCreator,
  bulkRejectAgentSourcedCreators,
  bulkRejectAgentSourcedCreatorsFailure,
  bulkRejectAgentSourcedCreatorsStatusReset,
  bulkRejectAgentSourcedCreatorsSuccess,
  fetchAgentSourcedCreators,
  fetchAgentSourcedCreatorsCount,
  fetchAgentSourcedCreatorsCountSuccess,
  fetchAgentSourcedCreatorsFailure,
  fetchAgentSourcedCreatorsSuccess,
  rejectAgentSourcedCreator,
  setSelectedRunScope,
  shortlistAgentSourcedCreator,
  undoAgentSourcedCreatorAction,
  unshortlistAgentSourcedCreator,
} from './actions'
import { IAgentSourcedCreatorState } from './types'

// BRAND-9515: the three verdict buckets a creator's `status` can occupy in a
// sourcing run's `counts`. `ready_for_shortlist` IS a real status (BRAND-9587
// corrected AgentSourcedCreatorStatus, which used to omit it) but is deliberately
// excluded here: these counts tally vibe-check verdicts, and that status is not
// a verdict. Consequence to know about: when a creator transitions INTO
// `ready_for_shortlist`, this guard skips both the decrement and the increment,
// so the local `approved` tally holds while the BE moves them to its own bucket —
// the run progress bar can therefore shift on the next full refetch. Pre-existing
// and out of BRAND-9587's scope; reconcile counts from the BE if it ever matters.
type VerdictCountBucket = 'pending' | 'approved' | 'rejected'

const isVerdictCountBucket = (status: string | undefined): status is VerdictCountBucket =>
  status === 'pending' || status === 'approved' || status === 'rejected'

// BRAND-9678: the three sticky bucket totals belong to ONE campaign. Both
// fetch entry points call this before doing anything else, so a fetch naming a
// different brief drops the previous brief's badges instead of leaving them on
// screen until something happens to overwrite them. A first fetch of the
// session (null) has no previous brief to leak from, and a refetch of the SAME
// brief must keep its totals (they are deliberately sticky across tabs).
const rebaseTotalsToCampaign = (state: IAgentSourcedCreatorState, campaignId: number): void => {
  if (state.totalsCampaignId !== null && state.totalsCampaignId !== campaignId) {
    state.vibeCheckTotal = 0
    state.interestedTotal = 0
    state.shortlistedTotal = 0
  }
  state.totalsCampaignId = campaignId
}

const initialState: IAgentSourcedCreatorState = {
  campaignId: null,
  creators: [],
  collectionLinks: [],
  metadata: null,
  listing: null,
  vibeCheckTotal: 0,
  interestedTotal: 0,
  shortlistedTotal: 0,
  totalsCampaignId: null,
  loading: false,
  error: null,
  actionLoading: {},
  selectedRunScope: null,
  pendingNewRunCampaignId: null,
  sourcingRuns: [],
  bulkRejectStatus: 'idle',
}

const agentSourcedCreatorsReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(fetchAgentSourcedCreators, (state, { payload }) => {
      // BRAND-9550 review fix: run state is per-CAMPAIGN, and until now
      // NOTHING reset it. `selectedRunScope` is written only by RunTabs (the
      // `?run=` deep link and pill clicks) and there is no slice-reset action,
      // so an explicit ordinal survived navigation to another campaign — where
      // it means a different run, or no run at all. `startDiscoveryRunSuccess`
      // clearing the scope back to null used to scrub that leak incidentally;
      // BRAND-9550 made it land on an explicit ordinal instead, which removed
      // the accidental mitigation and left the pre-existing leak (a pill click
      // always could do this) fully exposed: if the next campaign has fewer
      // runs, the BE's `select_run` returns [] and its list renders EMPTY.
      //
      // The fetch START is the right place, not the success: this is the one
      // action the slice sees that names a campaign, and fetchCreatorsSaga
      // selects `selectedRunScope` AFTER the reducers have run — so resetting
      // here also keeps the stale ordinal out of the REQUEST, not just off the
      // screen. Guarded on a genuine campaign-to-campaign transition: a
      // refetch of the SAME campaign (filter/sort change, pagination,
      // post-verdict refresh) must not yank a browsing user back to the
      // default run — that is exactly the BRAND-9515 complaint. A first fetch
      // (campaignId still null) has no previous campaign to leak from and is
      // likewise left alone.
      rebaseTotalsToCampaign(state, payload.campaignId)
      if (state.campaignId !== null && state.campaignId !== payload.campaignId) {
        state.selectedRunScope = null
        state.sourcingRuns = []
        state.pendingNewRunCampaignId = null
      }
      state.campaignId = payload.campaignId
      state.loading = true
      state.error = null
      // Clear the previous listing's data when starting a fresh (non-paginated)
      // fetch so stale rows from another tab don't render during the in-flight
      // window. Pagination (`append: true`) preserves the existing list.
      if (!payload.append) {
        state.creators = []
        state.collectionLinks = []
        state.metadata = null
      }
    })
    .addCase(fetchAgentSourcedCreatorsSuccess, (state, { payload }) => {
      state.loading = false
      state.error = null
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const incoming = payload.creators as any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state.creators = payload.append ? [...state.creators, ...incoming] : (incoming as any)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state.collectionLinks = payload.collectionLinks as any
      // `metadata.sourcing_runs[].links` carries IHateoasLink's readonly
      // `filter`/`sort_by` arrays, which Immer's WritableDraft mapped type
      // can't assign without a cast — same reasoning as collectionLinks above.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state.metadata = payload.metadata as any
      if (payload.listing) {
        state.listing = payload.listing
      }
      // Bucket totals are page-independent, so only (re)compute them on a fresh
      // (non-append) fetch. Paginated requests carry the filtered metadata.total
      // and no stats (stats are gated to the first page on the BE), so updating
      // on append would clobber the full-list total with a filtered count
      // (BRAND-9328).
      if (!payload.append) {
        // BRAND-9678: the same brief guard the counts warm-up applies (see
        // fetchAgentSourcedCreatorsCountSuccess) — a list response for a brief
        // the totals no longer belong to must not write them. `takeLatest`
        // does NOT cover this: leaving brief A's creators tab for a
        // NON-creators page of brief B (Inbox, Reports, PCR) still mounts the
        // campaign sidebar, whose counts warm-up rebases the totals to B,
        // while nothing on that page dispatches a list fetch to cancel A's
        // in-flight one. A response that names no campaign (token mode) can't
        // mismatch and is left alone.
        const totalsBelongToThisBrief =
          payload.campaignId === undefined ||
          state.totalsCampaignId === null ||
          state.totalsCampaignId === payload.campaignId
        // Preferred path: a single list response carries a `stats` block with all
        // three bucket totals, so one fetch keeps every sticky total in sync.
        const stats = payload.metadata?.stats
        if (!totalsBelongToThisBrief) {
          // Nothing to write — the badges on screen belong to another brief.
        } else if (stats) {
          if (typeof stats.vibe_check === 'number') {
            state.vibeCheckTotal = stats.vibe_check
          }
          if (typeof stats.interested === 'number') {
            state.interestedTotal = stats.interested
          }
          if (typeof stats.shortlisted === 'number') {
            state.shortlistedTotal = stats.shortlisted
          }
        } else if (payload.metadata?.total != null) {
          // Fallback path (stats absent): set only the active listing's sticky
          // total from metadata.total, as before.
          if (payload.listing === 'vibe-check') {
            state.vibeCheckTotal = payload.metadata.total
          } else if (payload.listing === 'interested') {
            state.interestedTotal = payload.metadata.total
          } else if (payload.listing === 'shortlisted') {
            state.shortlistedTotal = payload.metadata.total
          }
        }
        // Sourcing runs summary (BRAND-9306): BE sends this on the first page
        // only, like `stats`, so it's captured the same way — sticky, and
        // only overwritten when the field is actually present. `[]` is a
        // legitimate value here (a born-completed rerun with 0 creators) and
        // is truthy, so this only skips the truly-absent (undefined) case.
        if (payload.metadata?.sourcing_runs) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          state.sourcingRuns = payload.metadata.sourcing_runs as any
          // BRAND-9550: land on the run the user just started, now that the
          // default-selection contract no longer does it for us (see
          // startDiscoveryRunSuccess below). The just-started run is always
          // the newest, so the highest ordinal identifies it. Selecting it
          // EXPLICITLY is what the BRAND-9515 reducer comment already named
          // as the alternative to clearing the scope — that shortcut only
          // worked while the default resolved to the newest non-failed run,
          // which an `importing` run no longer wins.
          //
          // Review fix: the pending selection is armed FOR A CAMPAIGN and only
          // that campaign's response may consume it. It used to be consumed by
          // the first success carrying runs, whoever it was for — reachable
          // both when the post-start fetch fails (the flag stays armed until
          // some unrelated later fetch eats it) and when the user navigates
          // away before it resolves (the new mount's fetch cancels the old via
          // takeLatest and consumes it itself). A campaign the user started
          // nothing on would then be pinned to an ordinal it never chose,
          // possibly an importing/expired run, rendering the wrong empty
          // state. An absent payload campaign (token mode) never matches.
          const runs = payload.metadata.sourcing_runs
          if (
            state.pendingNewRunCampaignId !== null &&
            state.pendingNewRunCampaignId === payload.campaignId &&
            runs.length > 0
          ) {
            state.selectedRunScope = runs.reduce(
              (max, run) => Math.max(max, run.ordinal),
              runs[0].ordinal,
            )
            state.pendingNewRunCampaignId = null
          }
        }
      }
    })
    .addCase(fetchAgentSourcedCreatorsFailure, (state, { payload }) => {
      state.loading = false
      state.error = payload
    })
    // Counts-only warm-up START (BRAND-9678): rebases the sticky totals onto
    // the brief being warmed up. On the List/Shortlisted tabs this is the ONLY
    // action that names a campaign — no vibe-check list fetch happens there —
    // so without it a brief switch left all three badges showing the previous
    // brief's numbers, and only the two with a warm-up self-corrected once
    // their responses landed. MUST NOT touch anything else (see the
    // success-case contract below).
    .addCase(fetchAgentSourcedCreatorsCount, (state, { payload }) => {
      rebaseTotalsToCampaign(state, payload.campaignId)
    })
    // Counts-only warm-up: only mutates the matching sticky total.
    // MUST NOT touch creators, collectionLinks, loading, error, listing, or metadata.
    //
    // BRAND-9582 item 7: vibe-check is deliberately EXCLUDED here. Its warm-up
    // probe (fetchCounts.ts) hits the same run-scoped list endpoint as a real
    // fetch and reads only `metadata.total`, which — unlike interested/
    // shortlisted's unfiltered totals — is scoped to the currently SELECTED
    // run (AgentSourcedCreatorQueries.select_run narrows the main list/count
    // endpoint to one run; interested_for_brief/shortlisted_for_brief do not
    // narrow by run at all). The correct ALL-RUNS figure for the sidebar
    // badge only ever arrives via `metadata.stats.vibe_check` on a real list
    // fetch (see fetchAgentSourcedCreatorsSuccess above). Letting this warm-up
    // write vibeCheckTotal raced that stats-derived value and won
    // non-deterministically depending on response order — confirmed live on
    // QA (55, single-run, clobbering 111, all-runs). Ignoring `total` for
    // vibe-check closes the race unconditionally, regardless of dispatch
    // order.
    //
    // BRAND-9678 re-reads the SAME response's `metadata.stats.vibe_check`,
    // which is the unfiltered all-runs figure (bucket_stats, brief_id only)
    // and therefore not the value that caused 9582 — so Filters.tsx
    // dispatches the vibe-check warm-up again. The `total` exclusion below
    // stands exactly as 9582 left it; only the stats path is new.
    .addCase(fetchAgentSourcedCreatorsCountSuccess, (state, { payload }) => {
      // BRAND-9678: a response for a brief the totals no longer belong to is
      // dropped — a warm-up still in flight when the user switches briefs
      // would otherwise land the old brief's count on the new one.
      //
      // Known residual (Dal review, accepted): this compares WHICH BRIEF, not
      // which request, so it does not order two responses for the SAME brief
      // — revisit A→B→A fast enough and the first visit's response can land
      // after the second's and win (the watcher is takeEvery, by design: the
      // three per-mount warm-ups must not cancel each other). Both values are
      // that brief's own unfiltered counts seconds apart and the next mount
      // refreshes them, so this is left as-is rather than carrying a
      // per-listing request nonce for it.
      if (state.totalsCampaignId !== null && state.totalsCampaignId !== payload.campaignId) {
        return
      }
      // BRAND-9678: `stats.vibe_check` is the whole point of forwarding stats
      // here — it is the unfiltered all-runs figure (bucket_stats, brief_id
      // only), so the interested/shortlisted warm-ups that already run on
      // every brief open refresh the Vibe Check badge too, which is what
      // makes it correct on open instead of only after a click into the tab.
      if (typeof payload.stats?.vibe_check === 'number') {
        state.vibeCheckTotal = payload.stats.vibe_check
      }
      // interested/shortlisted deliberately keep their OWN `total` (Dal review
      // scope note). On this warm-up the two agree — the probe sends no
      // filters, and interested_for_brief/shortlisted_for_brief then reduce to
      // exactly the scopes bucket_stats counts — so sourcing them from `stats`
      // would change nothing today while silently making a FILTERED count read
      // unfiltered if one is ever dispatched. BRAND-9678 needs only the
      // vibe-check figure; these two are left exactly as they were.
      if (payload.listing === 'interested') {
        state.interestedTotal = payload.total
      } else if (payload.listing === 'shortlisted') {
        state.shortlistedTotal = payload.total
      }
    })
    .addCase(approveAgentSourcedCreator, (state, { payload }) => {
      state.actionLoading[payload.creatorId] = true
    })
    .addCase(rejectAgentSourcedCreator, (state, { payload }) => {
      state.actionLoading[payload.creatorId] = true
    })
    .addCase(undoAgentSourcedCreatorAction, (state, { payload }) => {
      state.actionLoading[payload.creatorId] = true
    })
    .addCase(shortlistAgentSourcedCreator, (state, { payload }) => {
      state.actionLoading[payload.creatorId] = true
    })
    .addCase(unshortlistAgentSourcedCreator, (state, { payload }) => {
      state.actionLoading[payload.creatorId] = true
    })
    .addCase(agentSourcedCreatorActionSuccess, (state, { payload }) => {
      state.actionLoading[payload.creatorId] = false
      const creator = state.creators.find((c) => c.id === payload.creatorId)
      // Capture the prior flag/status before mutating so counts are only
      // bumped on a genuine transition (guards against double-fires / no-op
      // re-confirms).
      const wasShortlisted = creator?.shortlisted === true
      const previousStatus = creator?.status
      const sourcingRunId = creator?.sourcing_run_id
      if (creator) {
        creator.status = payload.status
        if (payload.shortlisted !== undefined) {
          creator.shortlisted = payload.shortlisted
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        creator.links = payload.updatedLinks as any
      }
      // BRAND-9515: Ted, verbatim: "1 of 20 vibe checked — Count doesnt auto
      // reflect when I approve or reject a creator." RunTabs' record bar
      // ("X of N vibe checked") is derived entirely from
      // sourcingRuns[i].counts (see sourcingRunProgress.ts), which this
      // action previously never touched — it went stale until the next full
      // list refetch. The verdict endpoints (approve/reject/undo) don't
      // return refreshed counts in their response body (`{ id, status,
      // links }` — see approveCreator.ts/rejectCreator.ts/undoCreatorAction.ts),
      // so there's nothing from the BE to merge; this hand-reconciles by
      // decrementing the creator's previous status bucket and incrementing
      // the new one. Scoped to the run the creator was actually imported by
      // (`sourcing_run_id`), NOT "the selected run" — so this stays correct
      // if counts are ever rendered for more than one run at a time.
      if (
        creator &&
        sourcingRunId != null &&
        isVerdictCountBucket(previousStatus) &&
        isVerdictCountBucket(payload.status) &&
        previousStatus !== payload.status
      ) {
        const run = state.sourcingRuns.find((r) => r.id === sourcingRunId)
        if (run) {
          run.counts[previousStatus] = Math.max(0, run.counts[previousStatus] - 1)
          run.counts[payload.status] += 1
        }
        // If the run isn't in the slice (stale/unloaded), no-op — never
        // fabricate a run record just to hold a count.
      }
      // Reactively reconcile the sticky shortlist count and Shortlisted-list
      // membership from the BE response, so a heart toggle updates the sidebar
      // badge and the list without a page refresh (BRAND-9375). `shortlisted`
      // is only set by the shortlist/unshortlist actions, so approve/reject/undo
      // fall through untouched. We leave `interestedTotal` alone — a shortlisted
      // creator is still interested.
      if (payload.shortlisted !== undefined && payload.shortlisted !== wasShortlisted) {
        const becameShortlisted = payload.shortlisted === true
        state.shortlistedTotal = Math.max(0, state.shortlistedTotal + (becameShortlisted ? 1 : -1))
        // When viewing the Shortlisted listing, an unshortlisted creator no
        // longer belongs in the list — drop the row and keep ResultCount's
        // `total` in sync. Other listings (vibe-check/interested) legitimately
        // retain shortlisted creators, so we only prune here.
        if (!becameShortlisted && state.listing === 'shortlisted') {
          state.creators = state.creators.filter((c) => c.id !== payload.creatorId)
          if (state.metadata?.total != null) {
            state.metadata.total = Math.max(0, state.metadata.total - 1)
          }
        }
      }
    })
    .addCase(agentSourcedCreatorActionFailure, (state, { payload }) => {
      state.actionLoading[payload.creatorId] = false
    })
    // BRAND-9514 bulk reject: status only — the failure path deliberately
    // touches nothing else (all-or-nothing 422 = zero changes server-side;
    // the view keeps its selection so the user can adjust). The success path
    // also leaves the list alone: the saga re-fetches through the existing
    // flows rather than patching rows from the bulk response.
    .addCase(bulkRejectAgentSourcedCreators, (state) => {
      state.bulkRejectStatus = 'pending'
    })
    .addCase(bulkRejectAgentSourcedCreatorsSuccess, (state) => {
      state.bulkRejectStatus = 'succeeded'
    })
    .addCase(bulkRejectAgentSourcedCreatorsFailure, (state) => {
      state.bulkRejectStatus = 'failed'
    })
    .addCase(bulkRejectAgentSourcedCreatorsStatusReset, (state) => {
      state.bulkRejectStatus = 'idle'
    })
    .addCase(setSelectedRunScope, (state, { payload }) => {
      state.selectedRunScope = payload
    })
    // BRAND-9515 fix (stale list after starting a new run — Archer QA
    // repro): keep sourcingRuns in sync with every full brief-view snapshot,
    // not just the creator-list fetch's own metadata. A snapshot lands
    // FIRST in the start_discovery_run flow (startDiscoveryRunSaga puts
    // fetchCampaignSuccess, then startDiscoveryRunSuccess, then a follow-up
    // fetchAgentSourcedCreators) — syncing it here is the only way
    // sourcingRuns can already reflect the just-started run by the time that
    // follow-up fetch's saga resolves its own scope (see fetchCreatorsSaga's
    // getEffectiveSelectedRun resolution) and by the time this state is
    // rendered in the window before that fetch's response lands. Without
    // this, sourcingRuns stays one fetch behind — the tab strip/record bar
    // could show the just-started run as selected and importing while the
    // list underneath still resolved its own default against the PRIOR
    // (stale) runs list. Snapshots without a sourcing block (legacy/
    // non-sourcing briefs, partial campaign payloads) leave this untouched.
    .addCase(CampaignActionTypes.FETCH_SUCCESS, (state, action: any) => {
      const sourcingRuns = action.payload?.sourcing?.sourcing_runs
      if (sourcingRuns) {
        state.sourcingRuns = sourcingRuns
      }
    })
    // BRAND-9515: Ted, verbatim: "When I created a new run. It should go to
    // the latest run which is in progress. now it went back to Run 1" — an
    // explicit prior selection (e.g. a pill click on Run 1) otherwise stuck
    // around after a new run started.
    //
    // BRAND-9550 update: clearing to null used to be sufficient on its own,
    // because the default-selection contract was "newest NON-FAILED run" and
    // the just-started (`importing`) run won that. The contract now mirrors
    // the BE properly and prefers the newest run whose import COMPLETED, so
    // an importing run no longer wins the default and clearing alone would
    // send the user straight back to Run 1 — exactly the bug Ted reported.
    // The new run isn't in state yet at this point (it arrives with the next
    // fetch's `metadata.sourcing_runs`), so this arms a one-shot flag that
    // fetchAgentSourcedCreatorsSuccess consumes to select it explicitly.
    // Deep-link/explicit-selection behavior elsewhere is untouched.
    //
    // The flag records WHICH campaign the run was started on (review fix), so
    // only that campaign's response can consume it — see the consumption site
    // above for the two paths that otherwise mis-pin an unrelated campaign.
    .addCase(startDiscoveryRunSuccess, (state, { payload }) => {
      state.selectedRunScope = null
      state.pendingNewRunCampaignId = payload.campaignId
    })
})

export default agentSourcedCreatorsReducer
